import { useMemo, useState } from 'react';
import { IFiltersExecutive } from '../../../../../interfaces/excecutive/IFiltersExecutive';
import {
    useGetCapacityMetricsQuery,
    useGetUsageHistoryQuery,
} from '../../../../../store/excecutive/executiveApi';

// Hardcoded values for Glen Ivy
const GLEN_IVY_SUB = 'e28524d4-60b1-70c5-0228-8f8824193e20';
const GLEN_IVY_ASSISTANT_ID = '7ccb5e95-5f20-4a97-9e6c-5be584d43797';
const CAPACITY_TEST_SUB = '921544e4-20c1-703b-c1d4-62f60c91cf49';

const MonthToDateUsage = ({
    title,
    filters,
}: {
    title?: string;
    filters: IFiltersExecutive;
}) => {
    const {
        data: metrics,
        isLoading: loading,
        error: queryError,
        refetch,
    } = useGetCapacityMetricsQuery(
        {
            sub: CAPACITY_TEST_SUB,
            assistantId: GLEN_IVY_ASSISTANT_ID,
        },
        { pollingInterval: 7000 },
    );

    const [showHistoryModal, setShowHistoryModal] = useState(false);

    // Fetch usage history using RTK Query (only when modal is open)
    const {
        data: history = [],
        isLoading: historyLoading,
        error: historyError,
    } = useGetUsageHistoryQuery(
        { sub: GLEN_IVY_SUB, limit: 50 },
        { skip: !showHistoryModal }, // Only fetch when modal is open
    );

    const error = queryError
        ? queryError && typeof queryError === 'object' && 'data' in queryError
            ? (queryError.data as any)?.message ||
              'Failed to load usage metrics'
            : 'Failed to load usage metrics'
        : null;

    // Download CSV
    const handleDownloadCSV = async () => {
        try {
            // Use relative URL - RTK Query handles the base URL
            // The deviceAxiosQuery should have the base URL configured
            const response = await fetch(
                `/spa-voice-agent/usage/history/export/test?sub=${GLEN_IVY_SUB}`,
            );

            if (!response.ok) {
                throw new Error('Failed to export CSV');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `spa-usage-history-${GLEN_IVY_SUB}-${Date.now()}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Failed to download CSV:', error);
            alert('Failed to download CSV. Please try again.');
        }
    };

    // Format month range
    const formatMonthRange = (start: string, end: string) => {
        const startDate = new Date(start);
        const endDate = new Date(end);
        const startStr = startDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
        });
        const endStr = endDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
        return `${startStr} - ${endStr}`;
    };

    const resetText = useMemo(() => {
        const monthEndRaw =
            (metrics as any)?.currentUsage?.monthEnd ||
            (metrics as any)?.monthEnd ||
            null;

        if (!monthEndRaw) return '';

        const monthEnd = new Date(monthEndRaw);
        if (Number.isNaN(monthEnd.getTime())) return '';

        const day = monthEnd.getUTCDate();
        const monthName = monthEnd.toLocaleString('en-US', {
            month: 'long',
            timeZone: 'UTC',
        });
        const suffix =
            day % 10 === 1 && day !== 11
                ? 'st'
                : day % 10 === 2 && day !== 12
                  ? 'nd'
                  : day % 10 === 3 && day !== 13
                    ? 'rd'
                    : 'th';
        return `Resets at 00:00 on the ${day}${suffix} ${monthName}`;
    }, [metrics]);

    // Build usage data from real metrics
    const DATE_USAGE = useMemo(() => {
        if (!metrics) {
            return [
                {
                    label: 'Calls Month to date',
                    key: 'callsMonthDate',
                    symbol: '',
                    value: '0/1000',
                    percentage: 0,
                },
                {
                    label: 'Total Minutes',
                    key: 'total_minutes',
                    symbol: '',
                    value: '0/6900',
                    percentage: 0,
                },
            ];
        }

        const { currentUsage } = metrics;
        return [
            {
                label: 'Calls Month to date',
                key: 'callsMonthDate',
                symbol: '',
                value: `${currentUsage.calls}/${currentUsage.callsLimit}`,
                percentage: currentUsage.callsPercentage,
                remaining: currentUsage.callsRemaining,
            },
            {
                label: 'Total Minutes',
                key: 'total_minutes',
                symbol: '',
                value: `${currentUsage.minutes}/${currentUsage.minutesLimit}`,
                percentage: currentUsage.minutesPercentage,
                remaining: currentUsage.minutesRemaining,
            },
        ];
    }, [metrics]);

    if (loading) {
        return (
            <div className="mb-4">
                <div className="rounded-2xl border border-slate-200 p-6 text-center dark:bg-[#1F1F1F]">
                    <p className="text-gray-500 dark:text-gray-400">
                        Loading usage metrics...
                    </p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mb-4">
                <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center dark:bg-red-900/20 dark:border-red-800">
                    <p className="text-red-600 dark:text-red-400">
                        Error: {error}
                    </p>
                    <button
                        onClick={() => refetch()}
                        className="mt-2 text-sm text-red-600 underline dark:text-red-400">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="mb-4">
                <div className="mt-2 mb-2 flex flex-col md:flex-row flex-wrap md:items-center">
                    <h4
                    className="mb-2 text-[20px] leading-[20px] font-semibold tracking-[0px] text-[#101010] dark:text-white"
                        style={{ fontFamily: 'Inter, Arial, sans-serif' }}>
                        {title || 'Month to Date Usage'}
                    </h4>
                    {resetText && (
                        <div
                            className="md:ml-auto text-[#667085] dark:text-gray-400"
                            style={{
                                fontFamily: 'Inter, Arial, sans-serif',
                                fontWeight: 400,
                                fontSize: '14px',
                                lineHeight: '20px',
                                letterSpacing: '0%',
                            }}>
                            {resetText}
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap -mx-1">
                    {DATE_USAGE.map((call: any) => (
                        <div
                            key={call.key}
                            className="w-full md:w-6/12 px-1 mb-2 ">
                            <div
                                className="rounded-[14px] border border-[#00330F] h-[138px] px-5 py-4 flex flex-wrap content-start dark:bg-[#1F1F1F] overflow-visible">
                                <div className="flex items-center gap-1 w-full">
                                    <p
                                        className="text-sm font-semibold leading-[1.2] tracking-[0px] text-[#101010] dark:text-white"
                                        style={{
                                            fontFamily: 'Inter, Arial, sans-serif',
                                        }}>
                                        {call.label}
                                    </p>
                                    <div className="relative group">
                                        <button
                                            type="button"
                                            aria-label={`${call.label} info`}
                                            className="ml-1 mb-1 h-4 w-4 flex items-center justify-center">
                                            <img
                                                src="/svg/info-3.png"
                                                alt=""
                                                className="h-4 w-4 object-contain"
                                            />
                                        </button>
                                        <div className="pointer-events-none absolute right-0 top-7 z-20 w-56 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0f172a] p-2 text-xs text-gray-700 dark:text-gray-200 shadow-lg opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100">
                                            Reflects contracted capacity
                                        </div>
                                    </div>
                                </div>
                                <p
                                    className="mb-1 mt-2 w-full text-left text-[1.5rem] leading-[1.2] font-medium tracking-[0px] text-[#00330F] dark:text-white"
                                    style={{
                                        fontFamily:
                                            '"Plus Jakarta Sans", Inter, Arial, sans-serif',
                                        verticalAlign: 'middle',
                                    }}>
                                    {call.value}
                                </p>
                                {/* Progress bar */}
                                {call.percentage !== undefined && (
                                    <div className="w-full mt-3">
                                        <div className="w-full bg-[#E9EAEB] rounded-full h-[4px] dark:bg-gray-700">
                                            <div
                                                className={`h-[4px] rounded-full transition-all ${
                                                    call.percentage >= 100
                                                        ? 'bg-red-500'
                                                        : call.percentage >= 80
                                                        ? 'bg-yellow-500'
                                                        : 'bg-[#039855]'
                                                }`}
                                                style={{
                                                    width: `${Math.min(
                                                        100,
                                                        call.percentage,
                                                    )}%`,
                                                }}
                                            />
                                        </div>
                                        <div className="relative mt-2 h-[15px]">
                                            <p
                                                className="absolute left-0 text-[10px] leading-[15px] font-medium tracking-[0px] text-[#717680] dark:text-gray-400"
                                                style={{
                                                    fontFamily:
                                                        '"Plus Jakarta Sans", Inter, Arial, sans-serif',
                                                }}>
                                                {call.percentage.toFixed(1)}%
                                                {' '}
                                                Used
                                            </p>
                                            {call.remaining !== undefined && (
                                                <p
                                                    className="absolute left-1/2 -translate-x-1/2 text-[10px] leading-[15px] font-medium tracking-[0px] text-center whitespace-nowrap text-[#717680] dark:text-gray-400"
                                                    style={{
                                                        fontFamily:
                                                            '"Plus Jakarta Sans", Inter, Arial, sans-serif',
                                                    }}>
                                                    {call.remaining} Remaining
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Usage History Modal */}
            {showHistoryModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white dark:bg-[#1F1F1F] rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-semibold text-black dark:text-white">
                                Usage History
                            </h3>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleDownloadCSV}
                                    disabled={
                                        historyLoading || history.length === 0
                                    }
                                    className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed dark:bg-primary-dark dark:hover:bg-primary">
                                    Download CSV
                                </button>
                                <button
                                    onClick={() => setShowHistoryModal(false)}
                                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                    <svg
                                        className="w-6 h-6"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Modal Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {historyLoading ? (
                                <div className="text-center py-8">
                                    <p className="text-gray-500 dark:text-gray-400">
                                        Loading history...
                                    </p>
                                </div>
                            ) : historyError ? (
                                <div className="text-center py-8">
                                    <p className="text-red-600 dark:text-red-400">
                                        Failed to load usage history. Please try
                                        again.
                                    </p>
                                </div>
                            ) : history.length === 0 ? (
                                <div className="text-center py-8">
                                    <p className="text-gray-500 dark:text-gray-400">
                                        No usage history available yet.
                                    </p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200 dark:border-gray-700">
                                                <th className="text-left py-3 px-4 font-semibold text-black dark:text-white">
                                                    Period
                                                </th>
                                                <th className="text-right py-3 px-4 font-semibold text-black dark:text-white">
                                                    Calls
                                                </th>
                                                <th className="text-right py-3 px-4 font-semibold text-black dark:text-white">
                                                    Limit
                                                </th>
                                                <th className="text-right py-3 px-4 font-semibold text-black dark:text-white">
                                                    Overage
                                                </th>
                                                <th className="text-right py-3 px-4 font-semibold text-black dark:text-white">
                                                    Minutes
                                                </th>
                                                <th className="text-right py-3 px-4 font-semibold text-black dark:text-white">
                                                    Limit
                                                </th>
                                                <th className="text-right py-3 px-4 font-semibold text-black dark:text-white">
                                                    Overage
                                                </th>
                                                <th className="text-right py-3 px-4 font-semibold text-black dark:text-white">
                                                    Agents
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {history.map((record) => (
                                                <tr
                                                    key={record._id}
                                                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300">
                                                        {formatMonthRange(
                                                            record.monthStart,
                                                            record.monthEnd,
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-4 text-right text-gray-700 dark:text-gray-300">
                                                        {record.callsCount.toLocaleString()}
                                                    </td>
                                                    <td className="py-3 px-4 text-right text-gray-600 dark:text-gray-400">
                                                        {record.callsLimit.toLocaleString()}
                                                    </td>
                                                    <td className="py-3 px-4 text-right">
                                                        {record.callsOverage >
                                                        0 ? (
                                                            <span className="text-red-600 dark:text-red-400 font-semibold">
                                                                +
                                                                {record.callsOverage.toLocaleString()}
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-400">
                                                                -
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-4 text-right text-gray-700 dark:text-gray-300">
                                                        {record.minutesCount.toLocaleString()}
                                                    </td>
                                                    <td className="py-3 px-4 text-right text-gray-600 dark:text-gray-400">
                                                        {record.minutesLimit.toLocaleString()}
                                                    </td>
                                                    <td className="py-3 px-4 text-right">
                                                        {record.minutesOverage >
                                                        0 ? (
                                                            <span className="text-red-600 dark:text-red-400 font-semibold">
                                                                +
                                                                {record.minutesOverage.toLocaleString()}
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-400">
                                                                -
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-4 text-right text-gray-700 dark:text-gray-300">
                                                        {
                                                            record.activeAgentsCount
                                                        }
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MonthToDateUsage;
